import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getLeaderboard, getTags, getWinner } from '../api/gg'
import { Brand } from '../components/Brand'
import { useBranding } from '../store/branding'
import type { MonthlyWinner, ScoreEntry, Tag } from '../types'

const MODES = [
  { to: '/gallery', icon: '‹›', title: 'GALLERY', desc: 'Scroll left/right through the feed. Cinema-style slides.', color: 'border-kapow/50 hover:shadow-neon-cyan' },
  { to: '/tiles', icon: '▦', title: 'TILE', desc: 'Grid of thumbnails. Tap any tile to lightbox the full queue.', color: 'border-boom/50 hover:shadow-neon-violet' },
  { to: '/play', icon: '🔔', title: 'GOON MODE', desc: 'Timed tap round. Hit the buzzer. Rack combos. Bank the score.', color: 'border-pow/50 hover:shadow-neon-pink' },
  { to: '/memory', icon: '🃏', title: 'MEMORY', desc: 'Flip cards to find matching pairs from the NSFW tag feed.', color: 'border-zap/50' },
]

const TEASER_CARDS = [
  { label: 'NSFW', sub: 'LATE NIGHT TEMPTATION', img: '/teaser-lips.jpg', to: '/gallery' },
  { label: 'TEASE', sub: 'CLOSE BUT NOT ENOUGH', img: '/assets/goongallery-promo.jpg', to: '/tiles' },
  { label: 'EDGE', sub: 'PUSHING LIMITS', img: '/assets/grok-9cd0c44e-7b9e-42b1-9866-366d7eafdd7c.jpg', to: '/play' },
  { label: 'DISTRACTION', sub: 'CAN YOU FOCUS?', img: '/assets/grok-c1c3fd79-de5a-4b2c-8e0c-1f1710c1f67b.jpg', to: '/memory' },
]

const SIDEBAR = [
  { title: 'TAGS CHALLENGE', desc: 'Collect 50+ NSFW tags. Multipliers for rare combos.' },
  { title: 'TIMER & SCORE', desc: 'Beat your edge record. Live leaderboard.' },
  { title: 'BUZZER MODE', desc: 'Hit the buzzer when you peak. Bank it or lose it.' },
]

export function Home() {
  const branding = useBranding((s) => s.branding)
  const [topGoon, setTopGoon] = useState<ScoreEntry | null>(null)
  const [winner, setWinner] = useState<MonthlyWinner | null>(null)
  const [tags, setTags] = useState<Tag[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let live = true
    void getLeaderboard('all')
      .then((r) => { if (live) setTopGoon(r.entries[0] ?? null) })
      .catch(() => {})
      .finally(() => { if (live) setLoading(false) })
    void getWinner()
      .then((w) => { if (live) setWinner(w) })
      .catch(() => {})
    void getTags()
      .then((t) => { if (live) setTags(t.map((x) => ({ ...x, enabled: true, sort: 0 }))) })
      .catch(() => {})
    return () => { live = false }
  }, [])

  const goonName = topGoon ? topGoon.name ?? 'anon goon' : null

  return (
    <main className="min-h-[88vh] px-4 pb-16 pt-4">
      {/* HERO */}
      <section className="hero-cyber mx-auto max-w-5xl px-6 py-10 sm:px-10 sm:py-12">
        <div className="relative z-10 grid items-center gap-8 md:grid-cols-[1fr,auto]">
          <div className="text-center md:text-left">
            <Brand size="lg" className="mb-3 justify-center md:justify-start" />
            <span className="level-badge mb-4 inline-flex">
              <span>🏆</span> LEVEL UP YOUR DESIRE
            </span>

            <h1 className="goon-title text-[52px] leading-[0.88] tracking-[-1.5px] sm:text-[72px]">
              GAMIFY<br />YOUR GOON
            </h1>
            <p className="mt-3 text-lg text-[#f3c6ff]">
              Speed Through the Gallery, Hit the Buzzer for Points!
            </p>
            {branding.tagline && branding.tagline !== 'Tap fast. Score big.' ? (
              <p className="mt-1 text-sm text-muted">{branding.tagline}</p>
            ) : null}

            <Link
              to="/play"
              className="btn-goon mt-6 inline-flex items-center gap-2 text-xl px-12 py-3.5"
            >
              START GOONING SESSION <span className="text-2xl">›</span>
            </Link>
          </div>

          <div className="hidden md:flex flex-col items-center neon-hand select-none" aria-hidden>
            <div className="text-7xl">👆</div>
            <div className="mt-2 h-16 w-16 rounded-full border-2 border-boom bg-boom/20 shadow-neon-violet animate-hud-glow" />
            <p className="mt-2 text-[10px] uppercase tracking-[3px] text-boom/80">tap to goon</p>
          </div>
        </div>
      </section>

      <p className="mx-auto mt-5 max-w-3xl text-center text-[11px] uppercase tracking-[4px] text-muted/80">
        <span className="mr-3 inline-block h-px w-8 align-middle bg-edge" />
        GAMIFIED ADULT CONTENT PLATFORM
        <span className="ml-3 inline-block h-px w-8 align-middle bg-edge" />
      </p>

      {/* NSFW TAGS UP FRONT */}
      {tags.length > 0 ? (
        <section className="mx-auto mt-10 max-w-5xl">
          <div className="mb-3 flex items-center justify-center gap-2 text-xs uppercase tracking-[3px] text-pow">
            <span className="inline-block h-px w-8 bg-pow/40" /> NSFW TAGS <span className="inline-block h-px w-8 bg-pow/40" />
          </div>
          <div className="cyber-panel cyber-pink flex flex-wrap justify-center gap-2 p-4">
            {tags.map((t) => (
              <Link
                key={t.id}
                to="/play"
                className="rounded-lg border-2 border-pow/50 bg-pow/10 px-4 py-1.5 font-comic text-base uppercase tracking-wide text-white shadow-neon-pink transition hover:brightness-125"
              >
                {t.label}
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {/* FOUR MODES */}
      <section className="mx-auto mt-10 max-w-5xl">
        <div className="mb-4 flex items-center justify-center gap-2 text-xs uppercase tracking-[3px] text-muted">
          <span className="inline-block h-px w-8 bg-edge" /> PLAY MODES <span className="inline-block h-px w-8 bg-edge" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {MODES.map((m) => (
            <Link key={m.to} to={m.to} className={`mode-card ${m.color}`}>
              <div className="mode-card-icon">{m.icon}</div>
              <div className="font-comic text-xl tracking-wide text-white">{m.title}</div>
              <p className="mt-1 text-xs leading-snug text-muted">{m.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* GALLERY TEASER + sidebar panels from home mockup */}
      <section className="mx-auto mt-12 max-w-5xl">
        <div className="mb-4 flex items-center justify-center gap-2 text-xs uppercase tracking-[3px] text-muted">
          <span className="inline-block h-px w-8 bg-edge" /> GALLERY TEASER <span className="inline-block h-px w-8 bg-edge" />
        </div>
        <div className="grid gap-4 lg:grid-cols-[1fr,220px]">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {TEASER_CARDS.map((t, i) => (
              <Link
                key={i}
                to={t.to}
                className="group relative block aspect-[3/4] overflow-hidden rounded-2xl border-2 border-boom/40 bg-surface shadow-neon-violet transition hover:border-pow/60 hover:shadow-neon-pink active:scale-[0.985]"
              >
                <img src={t.img} alt="" className="absolute inset-0 h-full w-full object-cover opacity-80 group-hover:opacity-95 transition" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-pow/10" />
                <div className="relative flex h-full flex-col justify-between p-3">
                  <span className="w-fit rounded bg-pow px-2 py-px text-[10px] font-bold tracking-widest text-white ring-1 ring-pow">{t.label}</span>
                  <div className="text-sm font-bold text-white drop-shadow">{t.sub}</div>
                </div>
              </Link>
            ))}
          </div>
          <div className="hidden flex-col gap-2 lg:flex">
            {SIDEBAR.map((s, i) => (
              <div key={i} className="cyber-panel flex-1 p-3 text-xs">
                <div className="font-comic text-sm uppercase tracking-wide text-pow">{s.title}</div>
                <p className="mt-1 text-muted leading-snug">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leaderboard teaser */}
      <section className="mx-auto mt-12 max-w-3xl">
        <div className="cyber-panel cyber-pink p-6 text-center">
          <div className="comic-title text-3xl">🏆 BIGGEST GOON</div>
          {loading ? (
            <p className="mt-2 text-muted">SCANNING THE SESSION…</p>
          ) : goonName ? (
            <p className="mt-2">
              <span className="goon-title neon-pink text-4xl">{goonName}</span>
              <span className="ml-2 font-comic text-3xl text-white">{topGoon!.score.toLocaleString()} PTS</span>
            </p>
          ) : (
            <p className="mt-2 text-[#cfcfe6]">No goons yet — capture the crown.</p>
          )}
          {winner ? (
            <p className="mt-2 text-xs text-muted">
              LAST MONTH'S CHAMP: <span className="neon-cyan">{winner.name ?? 'a mystery gooner'}</span>
            </p>
          ) : null}
          <Link to="/scoreboard" className="btn-ghost mt-4 inline-block text-sm">VIEW LEADERBOARD →</Link>
        </div>
      </section>

      <p className="mt-10 text-center text-[10px] tracking-[3px] text-muted/60">CAPTURE. GOON. CLIMB.</p>
    </main>
  )
}